import { useState, useEffect, useRef } from "react";

interface GeoData {
  country: string;
  countryCode: string;
  city: string;
  loading: boolean;
  error: string | null;
}

const DEFAULT: GeoData = {
  country: "Angola",
  countryCode: "AO",
  city: "",
  loading: false,
  error: null,
};

const SESSION_KEY = "berastays_geo_cache";

/**
 * Lazy geolocation: doesn't block first paint.
 * - Returns Angola defaults instantly
 * - Background-fetches IP geolocation after 800ms idle
 * - Caches result in sessionStorage for the session
 * - Aborts on unmount
 */
export const useGeolocation = (options?: { defer?: boolean }) => {
  const defer = options?.defer ?? true;
  const [geoData, setGeoData] = useState<GeoData>(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) return { ...JSON.parse(cached), loading: false, error: null };
    } catch {}
    return DEFAULT;
  });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Skip if we already have a cached city
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) return;
    } catch {}

    const controller = new AbortController();
    const run = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
        if (!res.ok) throw new Error("geo failed");
        const data = await res.json();
        const next: GeoData = {
          country: data.country_name || "Angola",
          countryCode: data.country_code || "AO",
          city: data.city || "",
          loading: false,
          error: null,
        };
        setGeoData(next);
        try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(next)); } catch {}
      } catch {
        // Stay with defaults — no second-network round trip
      }
    };

    // Defer until the browser is idle so it doesn't block initial render
    if (defer) {
      const ric = (window as any).requestIdleCallback as
        | ((cb: () => void, opts?: { timeout: number }) => number)
        | undefined;
      if (ric) {
        ric(run, { timeout: 2000 });
      } else {
        setTimeout(run, 800);
      }
    } else {
      run();
    }

    return () => controller.abort();
  }, [defer]);

  return geoData;
};
